import { validateElfsightLink } from 'opensolar-pageeditor';

interface FileUploadObject {
  rawFile?: File;
  src?: string;
  title?: string;
  fieldName: string;
  itemId: string;
  propertyName?: string;
}

interface AssetField {
  rawFile?: File;
  [key: string]: any;
}

export const addAsset = (
  field: AssetField | AssetField[] | null | undefined,
  fileArr: FileUploadObject[],
  fieldName: string,
  itemId: string,
  propertyName?: string
): FileUploadObject[] => {
  if (field && field.constructor === Array && field.length) {
    const newUpload = field.filter((p) => p && p.rawFile && p.rawFile instanceof File)
    if (newUpload.length) fileArr.push({ ...newUpload[0], fieldName, itemId, propertyName })
  }
  return fileArr
}

export const getFileUploads = (assets: Record<string, any>): FileUploadObject[] => {
  let newFiles: FileUploadObject[] = []
  Object.entries(assets).forEach(([field, value]) => {
    if (!value) {
      return
    }

    if (field !== 'highlights') {
      newFiles = addAsset(value, newFiles, field, field, undefined)
      return
    }

    if (typeof value !== 'object') {
      return
    }

    Object.entries(value).forEach(([highlightId, highlightAsset]) => {
      if (!highlightAsset || typeof highlightAsset !== 'object') {
        return
      }

      Object.entries(highlightAsset).forEach(([assetProperty, assetValue]) => {
        if (!assetValue) {
          return
        }

        const files = addAsset(assetValue, [], field, highlightId, assetProperty)
        newFiles.push(...files)
      })
    })
  })
  return newFiles
}

interface ProcessedAsset {
  src?: string;
  title?: string;
}

type IdBasedAssets = Record<string, Record<string, any>>

interface ProcessedAssets {
  [fieldName: string]: ProcessedAsset | IdBasedAssets;
}

const processSingleUploadFiles = (singleUploadFiles: FileUploadObject[]): ProcessedAssets => {
  const processedAssets: ProcessedAssets = {}
  singleUploadFiles.forEach((uploadFile) => {
    processedAssets[uploadFile.fieldName] = {
      src: uploadFile.src,
      title: uploadFile.title,
    }
  })
  return processedAssets
}

const mergeExistingItemWithNewFiles = (
  existingItem: Record<string, any>,
  newFilesForIndex: FileUploadObject[]
): Record<string, any> => {
  const mergedItem: Record<string, any> = {}

  for (const [propertyName, propertyValue] of Object.entries(existingItem)) {
    if (propertyValue === undefined || propertyValue === null) {
      continue
    }
    if (typeof propertyValue === 'string') {
      mergedItem[propertyName] = propertyValue
    } else if (typeof propertyValue === 'object') {
      const matchingNewFile = newFilesForIndex.find(file => file.propertyName === propertyName)
      if (matchingNewFile) {
        mergedItem[propertyName] = {
          src: matchingNewFile.src,
          title: matchingNewFile.title,
        }
      }
    }
  }

  newFilesForIndex.forEach((newFile) => {
    if (newFile.propertyName && !(newFile.propertyName in existingItem)) {
      mergedItem[newFile.propertyName] = {
        src: newFile.src,
        title: newFile.title,
      }
    }
  })

  return mergedItem
}

const mergeIdBasedUploadFiles = (
  dictUploadFiles: FileUploadObject[],
  existingFieldAssets: IdBasedAssets | undefined
): IdBasedAssets => {
  // Collect all item IDs from uploads and existing assets
  const uploadIds = dictUploadFiles.map(file => file.itemId)
  const existingIds = existingFieldAssets ? Object.keys(existingFieldAssets) : []
  const allItemIds = new Set(uploadIds).union(new Set(existingIds))

  const processedDict: IdBasedAssets = {}
  allItemIds.forEach(itemId => {
    const existingItem = existingFieldAssets?.[itemId]
    const newFilesForItem = dictUploadFiles.filter(file => file.itemId === itemId)

    if (!existingItem) {
      const newItem: Record<string, any> = {}
      newFilesForItem.forEach(file => {
        if (file.propertyName) {
          newItem[file.propertyName] = {
            src: file.src,
            title: file.title,
          }
        }
      })
      if (Object.keys(newItem).length > 0) {
        processedDict[itemId] = newItem
      } else {
        processedDict[itemId] = {}
      }
    } else {
      processedDict[itemId] = mergeExistingItemWithNewFiles(existingItem, newFilesForItem)
    }
  })

  return processedDict
}

const processMultipleUploadFiles = (
  multipleUploadFiles: FileUploadObject[],
  existingAssets: Record<string, any>
): ProcessedAssets => {
  const processedAssets: ProcessedAssets = {}
  const ID_BASED_UPLOAD_FIELDS = ['highlights'] as const

  ID_BASED_UPLOAD_FIELDS.forEach((fieldName) => {
    const filesForField = multipleUploadFiles.filter(file => file.fieldName === fieldName)
    if (filesForField.length > 0) {
      const mergedAssets = mergeIdBasedUploadFiles(
        filesForField,
        existingAssets[fieldName]
      )

      if (Object.keys(mergedAssets).length > 0) {
        processedAssets[fieldName] = mergedAssets
      }
    }
  })

  return processedAssets
}

export const processFiles = (
  uploadFiles: FileUploadObject[],
  existingAssets: Record<string, any>
): { assets: ProcessedAssets } => {
  const singleUploadFiles = uploadFiles.filter(file => file.itemId === file.fieldName)
  const multipleUploadFiles = uploadFiles.filter(file => file.itemId !== file.fieldName)

  const singleUploadAssets = processSingleUploadFiles(singleUploadFiles)
  const multipleUploadAssets = processMultipleUploadFiles(multipleUploadFiles, existingAssets)

  return {
    assets: {
      ...singleUploadAssets,
      ...multipleUploadAssets
    }
  }
}

const uploadFields = {
  cover: 'cover_background',
  highlights: 'highlights',
}

export const formatSubmitValues = (values) => {
  if (!values.locale) values.locale = 'en'
  let elements = values.contents?.elements
  if (elements) {
    //clear data for disabled content blocks
    values.contents.elements = elements.map((x) => {
      if (!x.enabled && Object.keys(uploadFields).includes(x.contentType)) {
        const assetKey = uploadFields[x.contentType]
        if (!values.contents['assets']) values.contents['assets'] = {}
        values.contents['assets'][assetKey] = null
      }

      return { ...x, data: x.enabled ? x.data : undefined }
    })
  }
  return values
}

const youtubeRegex = /^(https?:\/\/)?((www\.)?youtube\.com|youtu\.be)\/.+$/
const vimeoRegex = /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/
export const validateVideoUrl = (value: string) => {
  if (value && !youtubeRegex.test(value) && !vimeoRegex.test(value)) {
    return 'Invalid video link. Please input a valid YouTube or Vimeo link.'
  }
}

export const validateRequiredVideoUrl = (value: string) => {
  if (!value) return 'This field is required'
  return validateVideoUrl(value)
}

export const validateElfsightWidgetLink = (value: string) => {
  if (!value) return 'This field is required'
  if (!validateElfsightLink(value.trim())) {
    return 'Invalid Elfsight widget link. Please input a valid Elfsight widget link.'
  }
}

export const validateExpoForm = (values) => {
  let errors = {}
  const elements = values?.contents?.elements
  if (elements) {
    let elementErrs = elements.map((element) => {
      if (element.contentType === 'stats' && element.data) {
        const invalidStats = element.data.filter((x) => !x.value || !x.description)
        if (invalidStats?.length) {
          let statErrs = element.data.map((stat) => {
            let objErr = {}
            if (!stat.value) objErr['value'] = 'This field is required'
            if (!stat.description) objErr['description'] = 'This field is required'
            return !stat.value || !stat.description ? objErr : undefined
          })
          return { data: statErrs }
        }
      }
      if (element.contentType === 'highlights' && element.data?.length) {
        let highlightErrs = element.data.map((highlight) => {
          let objErr = {}

          const isTitleMissing = !highlight.title
          const isCountdownMissingDate = highlight.enableCountdown && !highlight.countdownDate
          const highlightId = highlight.id
          const isPromoImageMissing = highlight.enablePromoImage && !values?.contents?.assets?.highlights?.[highlightId]?.promoImage

          if (isTitleMissing) objErr['title'] = 'This field is required'
          if (isCountdownMissingDate) objErr['countdownDate'] = 'This field is required'
          if (isPromoImageMissing) objErr['promoImage'] = 'This field is required'

          const hasErrors = isTitleMissing || isCountdownMissingDate || isPromoImageMissing
          return hasErrors ? objErr : undefined
        })
        return { data: highlightErrs }
      }
      return undefined
    })
    if (elementErrs.filter((x) => x)) {
      errors['contents'] = {
        elements: elementErrs,
      }
    }
  }
  if (!values.countries_include) {
    errors['countries_include'] = 'This field is required'
  }
  return errors
}
